import {useCallback, useContext, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {Button, Flex, Form, Input, Modal, Spin, Typography} from 'antd'
import {
    ApartmentOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    PaperClipOutlined,
    PlusOutlined,
    UploadOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import {NavContext} from '../../context/global_context'
import {deleteRequest, getRequest, postRequest} from '../../services/base'
import {notifyError, notifySuccess} from '../../utils/notifications'
import {downloadAsZip} from '../../components/connector_views/utils'
import HarnessLevelsSearchTable from './HarnessLevelsSearchTable'

import HarnessEditPanel from './HarnessEditPanel'
import EllipsisTooltip from '../common/EllipsisTooltip';

const ConnectorViewForm = ({ persisted, clone_from }) => {
  const { vehicle, connector_view } = useParams()
  const intl = useIntl();
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [value, dispatch] = useContext(NavContext)
  const [form] = Form.useForm()
  const [modal, contextHolder] = Modal.useModal()

  const svgInputRef = useRef(null)
  const pngInputRef = useRef(null)
  const referencesRef = useRef([])
  const panelChangesMadeRef = useRef(false)


  const [connectorView, setConnectorView] = useState(null)
  const [selectedHarnesses, setSelectedHarnesses] = useState([])
  const [checkedHarnessLevels, setCheckedHarnessLevels] = useState(new Set())
  const [allLevelsSelectedHarnessIds, setAllLevelsSelectedHarnessIds] =
    useState(new Set())
  const [expandedHarnessId, setExpandedHarnessId] = useState(null)

  const [showSummaryPanel, setShowSummaryPanel] = useState(false)
  const [hasUnsavedPanelChanges, setHasUnsavedPanelChanges] = useState(false)
  const [loading, setLoading] = useState(false)

  // File state
  const [svgFile, setSvgFile] = useState(null)
  const [pngFile, setPngFile] = useState(null)
  const [svgRemoved, setSvgRemoved] = useState(false)
  const [pngRemoved, setPngRemoved] = useState(false)
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false)

  // Set nav header
  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({id: 'layout.adminTools'}),
        sub_header: `${intl.formatMessage({ id: 'layout.vehicleList' })} / ${vehicle} / ${intl.formatMessage({ id: 'sidebar.vehicleViews' })} / ${connector_view ? intl.formatMessage({ id: 'connectorViews.form.editVehicleView' }) : intl.formatMessage({ id: 'connectorViews.form.newVehicleView' })}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: vehicle,
        back_btn_url: `/vehicles/${vehicle}/connector_views/`,
      },
    })
  }, [])

  // Load data for edit/clone
  useEffect(() => {
    if (connector_view) {
      loadConnectorViewData(connector_view)
    }
    if (searchParams.get('clone_from') && clone_from) {
      loadConnectorViewData(searchParams.get('clone_from'))
    }
  }, [])

  const loadConnectorViewData = async (id) => {
    setLoading(true)
    try {
      const res = await getRequest(`/api/connector_views/${id}/`)
      // base.js no longer rejects on a failed request, so the catch below only
      // sees genuine programming errors - the failure branch has to be explicit.
      if (res?.status === false) {
        notifyError(dispatch, intl.formatMessage({id: 'connectorViews.form.loadFailed'}))
        return
      }
      setConnectorView(res)
      setSelectedHarnesses(res?.harness_ids || [])
      setCheckedHarnessLevels(new Set(res?.harness_levels_ids || []))
      form.setFieldsValue({ name: res?.name })
      referencesRef.current = res?.group_ids || []
    } catch {
      notifyError(dispatch, intl.formatMessage({id: 'connectorViews.form.loadFailed'}))
    } finally {
      // Was a bare setLoading(false) after the try/catch, which an early return
      // from the guard above would have skipped - leaving the spinner up.
      setLoading(false)
    }
  }

  // SVG file handling — parse reference IDs
  const parseSvgReferences = useCallback((file) => {
    const reader = new FileReader()
    reader.addEventListener('load', (event) => {
      const parser = new DOMParser()
      const svgDoc = parser.parseFromString(
        event.target.result,
        'image/svg+xml'
      )
      const refs = []
      svgDoc.childNodes.forEach((main) => {
        main.childNodes.forEach((parent) => {
          parent.childNodes.forEach((child) => {
            if (child.id) refs.push(child.id)
          })
        })
      })
      referencesRef.current = refs
    })
    reader.readAsText(file)
  }, [])

  const handleSvgChange = useCallback(
    (e) => {
      const file = e.target.files?.[0]
      if (file) {
        setSvgFile(file)
        setSvgRemoved(false)
        parseSvgReferences(file)
      }
    },
    [parseSvgReferences]
  )

  const handlePngChange = useCallback((e) => {
    const file = e.target.files?.[0]
    if (file) {
      setPngFile(file)
      setPngRemoved(false)
    }
  }, [])

  // Clear file with confirmation
  const handleClearFile = useCallback(
    (type) => {
      modal.confirm({
        title: intl.formatMessage({id: 'connectorViews.form.removeTitle'}, {type: type.toUpperCase()}),
        content: intl.formatMessage({id: 'connectorViews.form.removeConfirmContent'}, {type: type.toUpperCase()}),
        okText: intl.formatMessage({id: 'connectorViews.form.remove'}),
        okType: 'danger',
        onOk: async () => {
          // Delete first, and only clear the form once the server has confirmed.
          // The local state used to be cleared up front, so a failed delete
          // showed an error and still removed the file name from the form.
          if (connectorView?.id && !(await deleteFile(type))) {
            return
          }
          if (type === 'svg') {
            setSvgFile(null)
            setSvgRemoved(true)
            referencesRef.current = []
          } else if (type === 'png') {
            setPngFile(null)
            setPngRemoved(true)
          }
        },
      })
    },
    [connectorView, modal, intl]
  )

  // Returns true only if the server confirmed the delete, so the caller knows
  // whether it may clear the file from the form.
  const deleteFile = async (type) => {
    try {
      const res = await deleteRequest(
        `/api/connector_views/delete_views/${connectorView.id}/${type}/`
      )
      // base.js no longer rejects on a failed request, so the catch below only
      // sees genuine programming errors. Without this branch a failed delete
      // fell through and reported success.
      if (res?.status === false) {
        notifyError(dispatch, intl.formatMessage({id: 'connectorViews.form.deleteFailed'}, {type: type.toUpperCase()}))
        return false
      }
      notifySuccess(dispatch, intl.formatMessage({id: 'connectorViews.form.deleteSuccess'}, {type: type.toUpperCase()}))
      loadConnectorViewData(connectorView.id)
      return true
    } catch {
      notifyError(dispatch, intl.formatMessage({id: 'connectorViews.form.deleteFailed'}, {type: type.toUpperCase()}))
      return false
    }
  }

  // Harness handlers
  const handleSelectHarness = useCallback((harness) => {
    panelChangesMadeRef.current = true
    setSelectedHarnesses((prev) => [...prev, harness])
    setExpandedHarnessId(harness.id)
  }, [])

  const handleClearHarness = useCallback(
    (harness, levels) => {
      panelChangesMadeRef.current = true
      setHasUnsavedPanelChanges(true)
      setSelectedHarnesses((prev) => prev.filter((h) => h.id !== harness.id))
      const levelIds = new Set(levels.map((l) => l.id))
      setCheckedHarnessLevels((prev) => {
        const next = new Set(prev)
        levelIds.forEach((id) => next.delete(id))
        return next
      })
      setAllLevelsSelectedHarnessIds((prev) => {
        const next = new Set(prev)
        next.delete(harness.id)
        return next
      })
      if (expandedHarnessId === harness.id) {
        setExpandedHarnessId(null)
      }
    },
    [expandedHarnessId]
  )

  const handleToggleExpand = useCallback((harnessId) => {
    setExpandedHarnessId((prev) => (prev === harnessId ? null : harnessId))
  }, [])

  // Level selection handler
  const handleSelectHarnessLevel = useCallback((levelIds, action) => {
    panelChangesMadeRef.current = true
    if (action === 'SELECT_ALL') {
      setCheckedHarnessLevels((prev) => {
        const next = new Set(prev)
        levelIds.forEach((id) => next.add(id))
        return next
      })
    } else if (action === 'DESELECT_ALL') {
      setCheckedHarnessLevels(new Set())
      setAllLevelsSelectedHarnessIds(new Set())
    } else {
      // Individual toggle
      setCheckedHarnessLevels((prev) => {
        const next = new Set(prev)
        const levelId = levelIds[0]
        if (next.has(levelId)) {
          next.delete(levelId)
        } else {
          next.add(levelId)
        }
        return next
      })
    }
  }, [])

  // Form submission
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()

      if (selectedHarnesses.length === 0) {
        notifyError(dispatch, intl.formatMessage({id: 'connectorViews.form.harnessRequired'}))
        return
      }

      setLoading(true)

      const formData = new FormData()
      formData.append('name', values.name)
      formData.append('vehicle_key', vehicle)
      formData.append('clone_from', clone_from ? 'true' : 'false')
      formData.append('png_removed', pngRemoved)
      formData.append('svg_removed', svgRemoved)
      formData.append('mapping_json_removed', 'false')

      // Harness data
      selectedHarnesses.forEach((h, i) => {
        if (i === 0) formData.append('harness', h.id)
        formData.append('harness_ids', h.id)
      })

      // Levels — either individual IDs or select-all flag
      const hasSelectAll = allLevelsSelectedHarnessIds.size > 0
      if (hasSelectAll) {
        formData.append('select_all_harness_levels', 'true')
        // Also include harness IDs that have select-all
        allLevelsSelectedHarnessIds.forEach((hId) => {
          formData.append('harness_ids', hId)
        })
      } else {
        formData.append('select_all_harness_levels', 'false')
        checkedHarnessLevels.forEach((levelId) => {
          formData.append('harness_levels_ids', levelId)
        })
      }

      // Files
      if (svgFile) formData.append('svg', svgFile)
      if (pngFile) formData.append('png', pngFile)

      // References from SVG
      referencesRef.current.forEach((ref) => {
        formData.append('references', ref)
      })

      // Edit mode
      if (persisted && connectorView?.id) {
        formData.append('id', connectorView.id)
      }

      // Clone mode
      if (clone_from && searchParams.get('clone_from')) {
        formData.append('clone_from_id', searchParams.get('clone_from'))
        formData.append('png_name', connectorView?.png_name || '')
        formData.append('svg_name', connectorView?.svg_name || '')
      }

      const res = await postRequest(
        '/api/connector_views/save_connector_view/',
        formData
      )

      if (res?.non_field_errors) {
        notifyError(dispatch, res.non_field_errors.toString())
      } else {
        setHasUnsavedPanelChanges(false)
        if (persisted && connectorView?.id && !clone_from) {
          notifySuccess(dispatch, intl.formatMessage({id: 'connectorViews.form.updateSuccess'}))
          if (svgInputRef.current) svgInputRef.current.value = null
          if (pngInputRef.current) pngInputRef.current.value = null
          setSvgFile(null)
          setPngFile(null)
          await loadConnectorViewData(connectorView.id)
        } else {
          notifySuccess(dispatch, intl.formatMessage({id: 'connectorViews.form.createSuccess'}))
          navigate(
            `/vehicles/${vehicle}/connector_views/${res?.id}/edit_mappings`
          )
        }
      }
    } catch (err) {
      if (err?.errorFields) {
        // Form validation failed — antd scrolls to the first error automatically
      } else {
        notifyError(dispatch, intl.formatMessage({id: 'connectorViews.form.saveError'}))
      }
    }
    setLoading(false)
  }, [
    form,
    vehicle,
    clone_from,
    persisted,
    connectorView,
    selectedHarnesses,
    checkedHarnessLevels,
    allLevelsSelectedHarnessIds,
    svgFile,
    pngFile,
    svgRemoved,
    pngRemoved,
    searchParams,
    navigate,
    intl,
  ])

  return (
    <div className="page connector-view-form">
      {contextHolder}
      <Spin spinning={loading}>
        <div className="connector-view-form-container">
          {/* Left Panel — Harness List */}
          <div className="harness-panel">
            <div className="harness-panel-header">
              <Typography.Text className='bold'>{intl.formatMessage({id: 'connectorViews.form.harnessSelection'})}</Typography.Text>
              <Flex align='center' gap={8}>
                {hasUnsavedPanelChanges && (
                  <Typography.Text type="warning" className='unsaved-changes-msg'>
                    <WarningOutlined className='unsaved-changes-msg-icon' />
                    {intl.formatMessage({id: 'connectorViews.form.unsavedHarnessChanges'})}
                  </Typography.Text>
                )}
                
                <Button
                  type='link'
                  size="small"
                  icon={<EditOutlined />}
                  className='link-button'
                  onClick={() => setShowSummaryPanel(true)}
                  data-testid="vv-edit-harnesses"
                >
                  {intl.formatMessage({id: 'forms.edit'})}
                </Button>

                {selectedHarnesses.length > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedHarnesses.length} {intl.formatMessage({id: 'forms.selected'})}
                  </Typography.Text>
                )}
              </Flex>
            </div>
            {selectedHarnesses.length > 0 ? (
              selectedHarnesses.map((harness) => (
                <HarnessLevelsSearchTable
                  key={harness.id}
                  harness={harness}
                  expandedHarnessId={expandedHarnessId}
                  onToggleExpand={handleToggleExpand}
                  onClearHarness={handleClearHarness}
                  checkedHarnessLevels={checkedHarnessLevels}
                  onSelectHarnessLevel={handleSelectHarnessLevel}
                  readOnly
                />
              ))
            ) : (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {intl.formatMessage({id: 'connectorViews.form.addHarnessPrompt'})}
                </Typography.Text>
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowSummaryPanel(true)}
                    data-testid="vv-open-harness-panel"
                  >
                    {intl.formatMessage({id: 'connectorViews.form.addHarness'})}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel — Form */}
          <div className="form-panel">
            <div className="vehicle-view-header">
              <Typography.Text className='bold'>{intl.formatMessage({id: 'connectorViews.form.vehicleView'})}</Typography.Text>
            </div>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              // requiredMark={false}
              className="connector-view-form-inner"
            >
              {/* Scrollable content area */}
              <div className="connector-view-form-content">
                {/* Name */}
                <Form.Item
                  name="name"
                  label={
                    <Typography.Text className='bold'>{intl.formatMessage({id: 'connectorViews.form.name'})}</Typography.Text>
                  }
                  rules={[{ required: true, message: intl.formatMessage({id: 'connectorViews.form.nameRequired'}) }]}
                  style={{marginLeft: '2px'}}
                >
                  <Input data-testid="vv-name" placeholder={intl.formatMessage({id: 'connectorViews.form.frontViewPlaceholder'})} />
                </Form.Item>

                {/* Image + File Uploads — side by side */}
                <div className="connector-view-media-row">
                  {/* Left: Current Image */}
                  <div className={`connector-view-image-col ${persisted && connectorView?.compressed_png ? 'img-exists' : ''}`}>
                    {persisted && connectorView?.compressed_png ? (
                      <>
                        <Typography.Text strong>{intl.formatMessage({id: 'connectorViews.form.currentImage'})}</Typography.Text>
                        <div
                          className="connector-view-current-image clickable-image"
                          onClick={() => setImagePreviewVisible(true)}
                        >
                          <img
                            src={connectorView.compressed_png}
                            alt={intl.formatMessage({id: 'connectorViews.form.currentViewAlt'})}
                          />
                        </div>
                        <Typography.Text
                          type="secondary"
                          style={{
                            fontSize: 12,
                            marginTop: 4,
                            display: 'block',
                            color: 'var(--color-instruction)'
                          }}
                        >
                          {intl.formatMessage({id: 'connectorViews.form.clickToViewFullSize'})}
                        </Typography.Text>
                      </>
                    ) : (
                      <div className="connector-view-current-image connector-view-image-placeholder">
                        <Typography.Text type="secondary">
                          {intl.formatMessage({id: 'connectorViews.form.noImageUploaded'})}
                        </Typography.Text>
                      </div>
                    )}
                  </div>

                  {/* Right: File Uploads */}
                  <div className="connector-view-uploads-col">
                    <Typography.Text className='bold'>{intl.formatMessage({id: 'connectorViews.form.images'})}</Typography.Text>
                    <div className="connector-view-file-upload">
                      {/* SVG Upload */}
                      <div className="file-upload-row">
                        <Typography.Text>SVG</Typography.Text>
                        <input
                          ref={svgInputRef}
                          type="file"
                          accept="image/svg"
                          data-testid="vv-svg-input"
                          onChange={handleSvgChange}
                          style={{ display: 'none' }}
                        />
                        {!svgFile && !connectorView?.svg_name && (
                          <Button
                            icon={<UploadOutlined />}
                            onClick={() => svgInputRef.current?.click()}
                          >
                            {intl.formatMessage({id: 'connectorViews.form.chooseSvg'})}
                          </Button>
                        )}
                        {svgFile && (
                          <div className="file-item">
                            <Flex gap={6} className='file-details'>
                              {!clone_from && (<PaperClipOutlined />)}
                              <span className="file-name" data-testid="vv-file-name">{svgFile.name}</span>
                              <DeleteOutlined
                                className="file-remove"
                                data-testid="vv-file-remove"
                                data-kind="svg"
                                onClick={() => handleClearFile('svg')}
                              />
                            </Flex>
                          </div>
                        )}
                        {!svgFile && connectorView?.svg_name && !svgRemoved && (
                          <div className="file-item">
                            <Flex gap={6} className='file-details'>
                              {!clone_from && (<PaperClipOutlined />)}
                              <span className="file-name" data-testid="vv-file-name">{connectorView.svg_name}</span>
                              {!clone_from && (
                                <DeleteOutlined
                                  className="file-remove"
                                  data-testid="vv-file-remove"
                                data-kind="svg"
                                onClick={() => handleClearFile('svg')}
                                />
                              )}
                            </Flex>
                          </div>
                        )}
                        {persisted && (
                          <div className="file-upload-warning">
                            {intl.formatMessage({id: 'connectorViews.form.svgWarning'})}
                          </div>
                        )}
                      </div>

                      {/* PNG Upload */}
                      <div className="file-upload-row">
                        <Typography.Text>PNG</Typography.Text>
                        <input
                          ref={pngInputRef}
                          type="file"
                          accept="image/png"
                          data-testid="vv-png-input"
                          onChange={handlePngChange}
                          style={{ display: 'none' }}
                        />
                        {!pngFile && !connectorView?.png_name && (
                          <Button
                            icon={<UploadOutlined />}
                            onClick={() => pngInputRef.current?.click()}
                          >
                            {intl.formatMessage({id: 'connectorViews.form.choosePng'})}
                          </Button>
                        )}
                        {pngFile && (
                          <div className="file-item">
                            <Flex gap={6} className='file-details'>
                              {!clone_from && (<PaperClipOutlined />)}
                              <span className="file-name" data-testid="vv-file-name">{pngFile.name}</span>
                              <DeleteOutlined
                                className="file-remove"
                                data-testid="vv-file-remove"
                                data-kind="png"
                                onClick={() => handleClearFile('png')}
                              />
                            </Flex>
                          </div>
                        )}
                        {!pngFile && connectorView?.png_name && !pngRemoved && (
                          <div className="file-item">
                            <Flex gap={6} className='file-details'>
                              {!clone_from && (<PaperClipOutlined />)}
                              <span className="file-name" data-testid="vv-file-name">{connectorView.png_name}</span>
                              {!clone_from && (
                                <DeleteOutlined
                                  className="file-remove"
                                  data-testid="vv-file-remove"
                                data-kind="png"
                                onClick={() => handleClearFile('png')}
                                />
                              )}
                            </Flex>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions — fixed at bottom */}
              <Flex gap={8} className="connector-view-actions">
                <div className="connector-view-actions-left">
                  {persisted && connectorView?.id && (
                    <Button
                      type="link"
                      className='link-button'
                      style={{flexGrow: 1, minWidth: 0}}
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        downloadAsZip(connectorView.id, connectorView.name, dispatch, intl)
                      }
                      data-testid="vv-form-download-zip"
                    >
                      <EllipsisTooltip className='ellipsis'>
                        {intl.formatMessage({id: 'connectorViews.form.downloadPersistedZip'})}
                      </EllipsisTooltip>
                    </Button>
                  )}
                  {persisted && connectorView?.id && (
                    <Button
                      type="link"
                      className='link-button'
                      style={{flexShrink: 1, minWidth: 0}}
                      icon={<ApartmentOutlined />}
                      onClick={() => navigate(`/vehicles/${vehicle}/connector_views/${connector_view}/edit_mappings`)}
                      data-testid="vv-form-edit-mappings"
                    >
                      <EllipsisTooltip className='ellipsis'>
                        {intl.formatMessage({id: 'connectorViews.list.editMappings'})}
                      </EllipsisTooltip>
                    </Button>
                  )}
                </div>
                <div 
                  className="connector-view-actions-right" 
                  style={{ maxWidth: `${persisted && connectorView?.id ? 'calc(45% - 8px)' : '100%'}`}}
                >
                  <Button  
                    style={{padding: '5px 12px', height: 'auto', minHeight: 32, flexShrink: 1}} 
                    onClick={() => navigate(`/vehicles/${vehicle}/connector_views`)}
                    data-testid="vv-cancel"
                  >
                    {intl.formatMessage({id: 'forms.cancel'})}
                  </Button>

                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    data-testid="vv-save"
                    style={{padding: '5px 8px', height: 'auto', minHeight: 32, flexGrow: 1, minWidth: 0}}
                  >
                    <EllipsisTooltip className='ellipsis'>
                      {persisted ? intl.formatMessage({id: 'connectorViews.form.saveChanges'}) : intl.formatMessage({id: 'connectorViews.form.addView'})}
                    </EllipsisTooltip>
                  </Button>
                </div>
              </Flex>
            </Form>
          </div>
        </div>
      </Spin>

      {/* Image Preview Modal */}
      <Modal
        open={imagePreviewVisible}
        title={intl.formatMessage({id: 'connectorViews.form.imagePreview'})}
        footer={null}
        onCancel={() => setImagePreviewVisible(false)}
        width="80vw"
        styles={{
          body: {
            textAlign: 'center',
            padding: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <img
          src={connectorView?.compressed_png}
          alt="Full size view"
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
        />
      </Modal>

      {/* Harness Edit Panel */}
      <HarnessEditPanel
        visible={showSummaryPanel}
        onClose={() => {
          setShowSummaryPanel(false)
          setHasUnsavedPanelChanges(panelChangesMadeRef.current)
          panelChangesMadeRef.current = false
        }}
        selectedHarnesses={selectedHarnesses}
        checkedHarnessLevels={checkedHarnessLevels}
        onSelectHarnessLevel={handleSelectHarnessLevel}
        onAddHarness={handleSelectHarness}
        onRemoveHarness={handleClearHarness}
        connectorViewName={connectorView?.name}
      />
    </div>
  )
}

export default ConnectorViewForm
